import { OzDate } from '../../../../shared/OzDate/OzDate';
import { IShopify } from '../../../../shared/Shopify/IShopify';
import {
    IAdditionalWinner,
    IImpactMedia,
    INonProfit,
} from '../../../ClosedExperience.state';

export interface ITransformedShopify {
    experience: {
        name: string;
        hasWinner: boolean;
        projectedWinnerAnnounceDate: Date;
        confirmedWinnerAnnounceDate: Date | undefined;
        prizeDetails: string;
        additionalWinners: IAdditionalWinner[];
    };
    winner: {
        name: string;
        location: string;
        image: string;
    };
    nonProfit: INonProfit;
    assets: {
        winnerBackgroundImage: string;
        winnerPendingBackgroundImage: string;
        ovalBackgroundDesktop: string;
        ovalBackgroundMobile: string;
    };
    impact: IImpactMedia;
}

export class ShopifyClosedExperienceTransformer {
    protected shopify: IShopify;

    public constructor (shopify: IShopify) {
        this.shopify = shopify;
    }

    public getShopify (): ITransformedShopify {
        return {
            experience: {
                name: this.shopify.experience.name,
                hasWinner: this.transformedHasWinner(),
                projectedWinnerAnnounceDate: this.transformedProjectedWinnerAnnounceDate(),
                confirmedWinnerAnnounceDate: this.transformedConfirmedWinnerAnnounceDate(),
                prizeDetails: this.shopify.experience.prizeDetails,
                additionalWinners: this.shopify.additionalWinners.winnerList,
            },
            winner: {
                name: this.shopify.winner.name,
                location: this.shopify.winner.location,
                image: this.shopify.winner.image,
            },
            nonProfit: {
                name: this.shopify.nonProfit.name,
                thankYouName: this.shopify.nonProfit.thankYouName,
                netRaiseAmount: this.shopify.nonProfit.netRaiseAmount,
                netRaiseCaption: this.shopify.nonProfit.netRaiseCaption,
                thankYouMessage: this.shopify.nonProfit.thankYouMessage,
                impactMessage: this.shopify.nonProfit.impactMessage,
                logoURL: this.shopify.nonProfit.logoURL,
            },
            impact: {
                headline: this.shopify.impact.headline,
                assets: this.transformImpactMediaAssets(this.shopify.impact.assets),
            },
            assets: {
                winnerBackgroundImage: this.shopify.assets.winnerBackgroundImage,
                winnerPendingBackgroundImage: this.shopify.assets.winnerPendingBackgroundImage,
                ovalBackgroundDesktop: this.shopify.assets.ovalBackgroundDesktop,
                ovalBackgroundMobile: this.shopify.assets.ovalBackgroundMobile,
            },
        };
    }

    protected transformedHasWinner (): boolean {
        return this.shopify.experience.hasWinner === 'true';
    }

    protected transformedProjectedWinnerAnnounceDate (): Date {
        return OzDate.getDateFromDateString(this.shopify.experience.projectedWinnerAnnounceDate);
    }

    protected transformedConfirmedWinnerAnnounceDate (): Date | undefined {
        const confirmedWinnerAnnounceDate: string = this.shopify.experience.confirmedWinnerAnnounceDate;

        if (OzDate.isTimestampValid(confirmedWinnerAnnounceDate)) {
            return OzDate.getDateFromDateString(confirmedWinnerAnnounceDate);
        }

        return undefined;
    }

    private transformImpactMediaAssets (assets: IImpactMediaAsset[]): IImpactMediaAsset[] {
        if (!assets) {
            return [];
        }

        return assets.map(this.transformImpactMediaAsset);
    }

    private transformImpactMediaAsset (mediaAsset: IImpactMediaAsset): IImpactMediaAsset {
        return {
            caption: mediaAsset.caption,
            imageURL: mediaAsset.imageURL,
            youtubeVideoLink: mediaAsset.youtubeVideoLink && mediaAsset.youtubeVideoLink.replace('watch?v=', 'embed/'),
        };
    }
}
